import { useMemo, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { completeTasks, archiveTasks, removeTasks } from 'actions/tasks';
import { updateTaskGroup, moveAllTaskGroupTasks, setTaskGroupVisibility, removeTaskGroup } from 'actions/task-groups';
import { updateMilestone, removeMilestone, setMilestoneVisibility, moveMilestone, tieMilestoneTasks } from 'actions/milestones';
import { tieMilestones } from 'actions/goals';
import { updateTaskGroup as updateTaskGroupApi, duplicateTaskGroup } from 'api/task-groups-api';
import { duplicateMilestone } from 'api/milestones-api';

import { useCanUser } from 'shared-hooks/permission';
import { useProjectModuleDisabled } from 'shared-hooks/project';
import { trackEvent } from 'services/events-tracker-service';
import { createAlert } from 'services/alert-service';

import Popout from 'components/popout';
import PopoutListActions from 'components/PopoutListActions';
import Tooltip from 'components/tooltip';
import ColorPicker from 'components/color-picker';
import SelectTaskGroupPopout from 'components/SelectTaskGroupPopout';
import SelectMilestonePopout from 'components/select-milestone-popout';
import HideFrom from 'components/hide-from';
import PromptPopout from 'components/prompt-popout';
import SelectCustomFieldPopout from 'components/SelectCustomFieldPopout';
import MilestoneTemplateCreateButton from 'components/MilestoneTemplateCreateButton';
import SelectProjectPopout from 'components/SelectProjectPopout';
import SelectGoalPopout from 'components/SelectGoalPopout';
import EditPanel from 'components/edit-panel';

import IconMoreHoriz from 'icons/icon-more-horiz';
import IconEdit from 'icons/icon-edit';
import IconStatus from 'icons/icon-status';
import IconPlus from 'icons/icon-plus';
import IconCheckCircled from 'icons/icon-check-circled';
import IconTransfer from 'icons/icon-transfer';
import IconEye from 'icons/icon-eye';
import IconRoadmap from 'icons/icon-roadmap';
import IconNumber from 'icons/icon-number';
import IconArchive from 'icons/icon-archive';
import IconRemove from 'icons/icon-remove';
import IconTemplate from 'icons/icon-template';
import IconGoal from 'icons/icon-goal';

const DEFAULT_ACTIONS = ['rename', 'changeColor', 'tieToGoal', 'duplicate', 'createTemplate', 'move', 'complete', 'moveAllTasks', 'hideFrom', 'setAsMilestone', 'unsetAsMilestone', 'setRollup', 'removeRollup', 'archiveTasks', 'delete', 'deleteArchivedTasks', 'archiveCompletedTasks'];

const EditName = ({ value, onSubmit, onShow, onHide }) => {
    const { t } = useTranslation('common');
    const [editing, setEditng] = useState(false);

    const handleClick = () => {
        setEditng(true);

        onShow?.();
    }

    const submit = value => {
        onSubmit?.(value);
        onHide?.();
        setEditng(false);
    }

    const handleSubmit = value => {
        submit(value);
    }

    const handleCancel = () => {
        submit(null);
    }

    if (!editing) {
        return (
            <div className="item-inner" onClick={handleClick}>
                <span className="item-icon"><IconEdit /></span>

                { t('common:Rename') }
            </div>
        );
    }

    return (
        <EditPanel
            placeholder={ t('common:Give it a name') }
            actionText={ t('common:Update') }
            defaultValue={value}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            noClose={true}
            autoFocus={true}
        />
    );
}

const GroupActions = ({ className = '', innerClassName = '', type, id, name, isStatic, milestoneId, projectId, tasks, rollup, color, hidden, allowedActions }) => {
    const { t } = useTranslation(['task', 'milestone', 'common']);
    const [visible, setVisible] = useState(false);
    const popoutCtrl = useRef(null);

    const canRemoveTask = useCanUser('removeTask');
    const canEditTask = useCanUser('editTask');
    const canCreateTaskGroup = useCanUser('createTaskGroup');
    const canCreateMilestone = useCanUser('createMilestone');
    const canEditTaskGroup = useCanUser('editTaskGroup');
    const canEditMilestone = useCanUser('editMilestone');
    const canRemoveTaskGroup = useCanUser('removeTaskGroup');
    const canRemoveMilestone = useCanUser('removeMilestone');
    const canCompleteTask = useCanUser('completeTask');
    const canMoveTask = useCanUser('moveTask');
    const canCreateTemplate = useCanUser('createTemplate');

    const milestonesDisabled = useProjectModuleDisabled(projectId, 'milestones');

    const canEditCurrentResource = useMemo(() => {
        switch (type) {
            case 'task_group':
                return canEditTaskGroup;

            case 'milestone':
                return canEditMilestone;

            default:
                return false;
        }
    }, [type, canEditTaskGroup, canEditMilestone]);

    const canRemoveCurrentResource = useMemo(() => {
        switch (type) {
            case 'task_group':
                return canRemoveTaskGroup;

            case 'milestone':
                return canRemoveMilestone;

            default:
                return false;
        }
    }, [type, canEditTaskGroup, canEditMilestone]);

    const hasTasks = useMemo(() => Boolean(tasks?.length), [tasks]);

    const actions = useMemo(() => {
        return (allowedActions || DEFAULT_ACTIONS).filter(action => {
            switch (action) {
                case 'rename':
                    return !isStatic && canEditCurrentResource;

                case 'changeColor':
                    return !isStatic && canEditCurrentResource;

                case 'duplicate':
                    return !isStatic && ((type === 'task_group' && canCreateTaskGroup) || (type === 'milestone' && canCreateMilestone));

                case 'tieToGoal':
                    return !isStatic && type === 'milestone' && canEditMilestone;

                case 'createTemplate':
                    return !isStatic && type === 'milestone' && canCreateTemplate;

                case 'move':
                    return !isStatic && type === 'milestone' && canEditMilestone;

                case 'complete':
                    return !isStatic && hasTasks && canCompleteTask

                case 'moveAllTasks':
                    return !isStatic && hasTasks && canMoveTask;

                case 'hideFrom':
                    return !isStatic && canEditCurrentResource;

                case 'setAsMilestone':
                    return !isStatic && type === 'milestone' && canEditMilestone && !milestoneId && !milestonesDisabled;

                case 'unsetAsMilestone':
                    return !isStatic && type === 'milestone' && canEditMilestone && milestoneId && !milestonesDisabled;

                case 'setRollup':
                    return !isStatic && type === 'task_group' && canEditTaskGroup && !rollup?.fieldId

                case 'removeRollup':
                    return !isStatic && type === 'task_group' && canEditTaskGroup && rollup?.fieldId;

                case 'archiveTasks':
                    return !isStatic && hasTasks && canEditTask;

                case 'delete':
                    return !isStatic && canRemoveCurrentResource;

                case 'deleteArchivedTasks':
                    return type === 'task_group' && id === 'archived' && projectId && canRemoveTask;

                case 'archiveCompletedTasks':
                    return type === 'task_group' && id === 'completed' && projectId && canEditTask;

                default:
                    return true;

            }
        });
    }, [allowedActions, isStatic, type, milestoneId, hasTasks, rollup, projectId, canRemoveTask, canEditTask, canCreateTaskGroup, canCreateMilestone, canEditTaskGroup, canEditMilestone, canCompleteTask, canMoveTask, canEditCurrentResource, canRemoveCurrentResource]);

    const hidePopout = useCallback(() => {
        popoutCtrl.current?.hide();
    }, [popoutCtrl]);

    const handleTriggerClick = () => {
        popoutCtrl.current?.show();
    };

    const handleShow = useCallback(() => {
        setVisible(true);
    }, []);

    const handleHide = useCallback(() => {
        setVisible(false);
    }, []);

    const handleNameSubmit = useCallback(name => {
        if (name === null || name === '') {
            return;
        }

        if (type === 'task_group') {
            updateTaskGroup(id, { name });
        } else {
            updateMilestone(id, { name });
        }

        hidePopout();
    }, [type, id, hidePopout]);

    const handleColorChange = useCallback(color => {
        if (type === 'task_group') {
            updateTaskGroup(id, { color });
        } else {
            updateMilestone(id, { color });
        }

        hidePopout();
    }, [type, id, hidePopout]);

    const handleDuplicateClick = useCallback(() => {
        if (type === 'task_group') {
            duplicateTaskGroup(id);
        } else {
            duplicateMilestone(id);
        }

        hidePopout();
    }, [type, id, hidePopout]);

    const handleTieToGoalSelect = useCallback(({ id: goalId }) => {
        tieMilestones(goalId, [id]);

        hidePopout();
    }, [id, hidePopout]);

    const handleCreateTemplateClick = useCallback(() => {
        hidePopout();
    }, [hidePopout]);

    const handleProjectSelect = useCallback(async ({ id: projectId }) => {
        await moveMilestone(id, projectId);

        hidePopout();

        createAlert('success', t('milestone:alerts.moved'));
    }, [id, hidePopout, t]);

    const handleCompleteAllTasks = useCallback(() => {
        trackEvent('TASKLIST.COMPLETE');

        if (tasks.length) {
            completeTasks(tasks.map(task => task.id));
        }

        hidePopout();
    }, [hidePopout, tasks]);

    const handleMoveTaskGroupTasks = useCallback(({ id: taskGroupId, projectId }) => {
        moveAllTaskGroupTasks(id, taskGroupId, projectId);
        hidePopout();
    }, [id, hidePopout]);

    const handleMoveListTasks = useCallback(({ id: milestoneId, projectId }) => {
        tieMilestoneTasks(milestoneId, tasks.map(task => task.id));
        hidePopout();
    }, [id, tasks, hidePopout]);

    const handleResourceHide = useCallback(roleIds => {
        if (type === 'task_group') {
            setTaskGroupVisibility(id, false, roleIds);
        } else {
            setMilestoneVisibility(id, false, roleIds);
        }

        hidePopout();
    }, [type, id, hidePopout]);

    const handleResourceReveal = useCallback(roleIds => {
        if (type === 'task_group') {
            setTaskGroupVisibility(id, true, roleIds);
        } else {
            setMilestoneVisibility(id, true, roleIds);
        }

        hidePopout();
    }, [type, id, hidePopout]);

    const handleSetAsMilestone = useCallback(() => {
        updateMilestone(id, {
            is_list: false
        });

        hidePopout();
    }, [id, hidePopout]);

    const handleUnsetAsMilestone = useCallback(() => {
        updateMilestone(id, {
            is_list: true
        });

        hidePopout();
    }, [id, hidePopout]);

    const handleSelectRollupField = useCallback(fieldId => {
        updateTaskGroupApi(id, { rollup: fieldId });
        hidePopout();
    }, [id, hidePopout]);

    const handleRemoveRollupField = useCallback(() => {
        updateTaskGroupApi(id, { rollup: null });
        updateTaskGroup(id, { rollup: null }, true);

        hidePopout();
    }, [id, hidePopout]);

    const handleArchiveTasksClick = useCallback(() => {
        if (type === 'task_group') {
            archiveTasks({
                task_group_id: id
            });
        } else {
            archiveTasks({
                task_ids: tasks.map(task => task.id),
                archived: true
            });   
        }

        hidePopout();
    }, [hidePopout, id, tasks]);

    const handleArchiveAndDeleteClick = useCallback(async () => {
        if (type === 'task_group') {
            trackEvent('TASKLIST.REMOVE');

            try {
                await removeTaskGroup(id);
            } catch (e) {}
        } else {
            try {
                await removeMilestone(id);
            } catch (e) {}

        }

        hidePopout();
    }, [hidePopout, id, tasks]);

    const handleDeleteArchivedTasksClick = useCallback(() => {
        removeTasks({
            project_id: projectId,
            archived: true
        });

        hidePopout();
    }, [hidePopout, projectId]);

    const handleArchiveCompletedTasksClick = useCallback(() => {
        archiveTasks({
            project_id: projectId,
            completed: true
        });

        hidePopout();
    }, [hidePopout, projectId]);

    const renderAction = useCallback((actionId, onExpand, onCollapse) => {
        switch (actionId) {
            case 'rename':
                return (
                    <EditName
                        value={name}
                        onSubmit={handleNameSubmit}
                        onShow={onExpand}
                        onHide={onCollapse}
                    />
                );

            case 'changeColor':
                return (
                    <ColorPicker
                        inline={true}
                        xAlign="right"
                        defaultXPosition="left"
                        withTransparent={true}
                        selected={color}
                        onSelect={handleColorChange}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconStatus /></span>

                            { t('actions.changeColor.title') }
                        </div>
                    </ColorPicker>
                );

            case 'duplicate':
                return (
                    <div className="item-inner" onClick={handleDuplicateClick}>
                        <span className="item-icon"><IconPlus /></span>

                        { type === 'task_group' ? t('actions.Duplicate status') : t('milestone:actions.Duplicate List') }
                    </div>
                );

            case 'tieToGoal':
                return (
                    <SelectGoalPopout
                        title={ t('actions.tieToGoal.title') }
                        excluded={[id]}
                        projectId={projectId}
                        inline={true}
                        xAlign="right"
                        defaultXPosition="left"
                        onSelect={handleTieToGoalSelect}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconGoal /></span>

                            { t('actions.tieToGoal.title') }
                        </div>
                    </SelectGoalPopout>
                );

            case 'createTemplate':
                return (
                    <MilestoneTemplateCreateButton id={id} className="item-inner" onClick={handleCreateTemplateClick}>
                        <span className="item-icon"><IconTemplate /></span>

                        { t('template:save') }
                    </MilestoneTemplateCreateButton>
                );

            case 'move':
                return (
                    <SelectProjectPopout
                        title={ t('milestone:actions.move.title') }
                        selected={projectId}
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                        onSelect={handleProjectSelect}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconTransfer /></span>

                            { t('milestone:actions.move.title') }
                        </div>
                    </SelectProjectPopout>
                );

            case 'complete':
                return (
                    <div className="item-inner" onClick={handleCompleteAllTasks}>
                        <span className="item-icon"><IconCheckCircled /></span>

                        { t('actions.Complete all tasks') }
                    </div>
                );

            case 'moveAllTasks':
                if (type === 'task_group') {
                    return (
                        <SelectTaskGroupPopout
                            title={ t('actions.Move tasks') }
                            xAlign="right"
                            defaultXPosition="left"
                            selected={id}
                            projectId={projectId}
                            onSelect={handleMoveTaskGroupTasks}
                            onShow={onExpand}
                            onHide={onCollapse}
                        >
                            <div className="item-inner">
                                <span className="item-icon"><IconTransfer /></span>

                                { t('actions.Move tasks') }
                            </div>
                        </SelectTaskGroupPopout>
                    )
                }

                return (
                    <SelectMilestonePopout
                        title={ t('actions.Move tasks') }
                        projectId={projectId}
                        selected={id}
                        inline={true}
                        isList={true}
                        xAlign="right"
                        defaultXPosition="left"
                        onSelect={handleMoveListTasks}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconTransfer /></span>

                            { t('actions.Move tasks') }
                        </div>
                    </SelectMilestonePopout>
                );

            case 'hideFrom':
                return (
                    <HideFrom
                        hidden={hidden}
                        xAlign="right"
                        defaultXPosition="left"
                        inline={true}
                        onResourceHide={handleResourceHide}
                        onResourceReveal={handleResourceReveal}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconEye /></span>

                            { type === 'task_group' ? t('actions.Hide status from') : t('milestone:actions.Hide List from') }
                        </div>
                    </HideFrom>
                );

            case 'setAsMilestone':
                return (
                    <div className="item-inner" onClick={handleSetAsMilestone}>
                        <span className="item-icon"><IconRoadmap /></span>

                        { t('milestone:actions.setAsMilestone') }
                    </div>
                );

            case 'unsetAsMilestone':
                return (
                    <div className="item-inner" onClick={handleUnsetAsMilestone}>
                        <span className="item-icon"><IconRoadmap /></span>

                        { t('milestone:actions.unsetAsMilestone') }
                    </div>
                );

            case 'setRollup':
                return (
                    <SelectCustomFieldPopout
                        title={ t('actions.setRollup.popoutTitle') }
                        xAlign="left"
                        defaultXPosition="right"
                        inline={true}
                        association="task" 
                        selected={rollup?.fieldId}
                        projectId={projectId}
                        onSelect={handleSelectRollupField}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconNumber /></span>

                            { t('actions.setRollup.title') }
                        </div>
                    </SelectCustomFieldPopout>
                );

            case 'removeRollup':
                return (
                    <div className="item-inner" onClick={handleRemoveRollupField}>
                        <span className="item-icon"><IconNumber /></span>

                        { t('actions.removeRollup.title') }
                    </div>
                );

            case 'archiveTasks':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ t('actions.archiveAll.title') }
                        text={ t('actions.archiveAll.description') }
                        confirmText={ t('actions.archiveAll.confirm') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleArchiveTasksClick}>
                            <span className="item-icon"><IconArchive /></span>

                            { t('actions.archiveAll.title') }
                        </div>
                    </PromptPopout>
                );

            case 'delete':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ type === 'task_group' ? t('actions.removeAndArchiveTaskGroup.title') : t('milestone:actions.removeAndArchive.title') }
                        text={ type === 'task_group' ? t('actions.removeAndArchiveTaskGroup.description') : t('milestone:actions.removeAndArchive.description') }
                        confirmText={ type === 'task_group' ? t('actions.removeAndArchiveTaskGroup.confirm') : t('milestone:actions.removeAndArchive.confirm') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleArchiveAndDeleteClick}>
                            <span className="item-icon"><IconRemove /></span>

                            { type === 'task_group' ? t('actions.removeAndArchiveTaskGroup.title') : t('milestone:actions.removeAndArchive.title') }
                        </div>
                    </PromptPopout>
                );

            case 'deleteArchivedTasks':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ t('actions.deleteArchivedTasks.title') }
                        text={ t('actions.deleteArchivedTasks.description') }
                        confirmText={ t('actions.deleteArchivedTasks.confirm') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleDeleteArchivedTasksClick}>
                            <span className="item-icon"><IconRemove /></span>

                            { t('actions.deleteArchivedTasks.title') }
                        </div>
                    </PromptPopout>
                );

            case 'archiveCompletedTasks':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ t('actions.archiveCompletedTasks.title') }
                        text={ t('actions.archiveCompletedTasks.description') }
                        confirmText={ t('actions.archiveCompletedTasks.confirm') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleArchiveCompletedTasksClick}>
                            <span className="item-icon"><IconArchive /></span>

                            { t('actions.archiveCompletedTasks.title') }
                        </div>
                    </PromptPopout>
                );

            default:
                return null;
        }
    }, [id, name, type, color, projectId, hidden, handleNameSubmit, handleColorChange, handleDuplicateClick, handleTieToGoalSelect, handleCreateTemplateClick, handleProjectSelect, handleCompleteAllTasks, handleMoveTaskGroupTasks, handleMoveListTasks, handleResourceHide, handleResourceReveal, handleSetAsMilestone, handleUnsetAsMilestone, handleSelectRollupField, handleRemoveRollupField, handleArchiveTasksClick, handleArchiveAndDeleteClick, handleDeleteArchivedTasksClick, handleArchiveCompletedTasksClick, t]);

    if (!actions.length) {
        return null;
    }

    return (
        <div className={className + (visible ? ' visible' : '')} onClick={handleTriggerClick}>
            <Tooltip tooltip={t('common:More options')} withTooltipHover={false}>
                <span className={innerClassName}><IconMoreHoriz /></span>
            </Tooltip>

            <Popout
                className="xsmall"
                onShow={handleShow}
                onHide={handleHide}
                provideController={popoutCtrl}
             >
                <PopoutListActions actions={actions} renderAction={renderAction} updatePopoutPosition={popoutCtrl.current?.updatePosition} />
            </Popout>
        </div>
    );
}

export default GroupActions;
